import React, { createContext, useContext, useCallback, useRef } from "react";
import { Order } from "../../models/csos/order";

type Listener<T> = (value: T) => void;

function createSubject<T>() {
  const listeners: Set<Listener<T>> = new Set();
  return {
    subscribe: (listener: Listener<T>) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    next: (value: T) => {
      listeners.forEach((l) => l(value));
    },
  };
}

export function createOrderDetailsServiceValue() {
  const orderDetailsSource = createSubject<Order>();
  const resetTimerSource = createSubject<string>();
  const refreshOrderSource = createSubject<string>();
  const openSignApproveSource = createSubject<string>();
  const quantitySource = createSubject<boolean>();
  const showorderDetailModalSource = createSubject<string>();
  const showtimerSource = createSubject<string>();
  const announcestartTimerSource = createSubject<Order>();
  const signedTabrefreshSource = createSubject<string>();
  const totalNumberOfOrdersSource = createSubject<number>();

  return {
    // Observables
    orderAnnounced$: orderDetailsSource,
    resetTime$: resetTimerSource,
    refreshOrder$: refreshOrderSource,
    openSignApprove$: openSignApproveSource,
    quantityUpdate$: quantitySource,
    closeOrderDetail$: showorderDetailModalSource,
    showTimeoutModal$: showtimerSource,
    announcestartTimer$: announcestartTimerSource,
    signedTabrefresh$: signedTabrefreshSource,
    totalNumberOrders$: totalNumberOfOrdersSource,

    // Methods
    announceOrder: (order: Order) => orderDetailsSource.next(order),
    resetTimer: (reset: string) => resetTimerSource.next(reset),
    refreshorder: (refresh: string) => refreshOrderSource.next(refresh),
    openSignApprove: (openSignLayer: string) => openSignApproveSource.next(openSignLayer),
    quantityUpdate: (isEdited: boolean) => quantitySource.next(isEdited),
    announceDetailClose: (close: string) => showorderDetailModalSource.next(close),
    showTimerModal: (show: string) => showtimerSource.next(show),
    announcestartTimer: (order: Order) => announcestartTimerSource.next(order),
    SignedTabRefresh: (refreshsigned: string) => signedTabrefreshSource.next(refreshsigned),
    TotalPendingOrder: (numberOfOrder: number) => totalNumberOfOrdersSource.next(numberOfOrder),
  };
}

export type OrderDetailsServiceType = ReturnType<typeof createOrderDetailsServiceValue>;

export const OrderDetailsContext = createContext<OrderDetailsServiceType | null>(null);

export function useOrderDetailsService(): OrderDetailsServiceType {
  const ctx = useContext(OrderDetailsContext);
  if (!ctx) throw new Error("OrderDetailsContext not provided");
  return ctx;
}

export const OrderDetailsProvider: React.FC = ({ children }) => {
  const serviceRef = useRef(createOrderDetailsServiceValue());
  return (
    <OrderDetailsContext.Provider value={serviceRef.current}>
      {children}
    </OrderDetailsContext.Provider>
  );
};
